// Models
import { DayOfWeek, RelativeWeekIndex } from "@ms/uno-constants/lib/local/DateConstants";
import { RecurrencePatternType } from "../../../service/planner/enumerations/IPlannerEnumerations";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import isEmpty from "lodash/isEmpty";
/**
 * The recurrence pattern implementation.
 */ export class RecurrencePattern {
    static fromResource(resource) {
        // Graph sends defaults for unused properties per type. We store nulls, thus we need to start with null and set per type.
        const interval = resource.interval;
        switch(resource.type){
            case RecurrencePatternType.Daily:
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.Daily,
                    interval,
                    dayOfMonth: null,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    month: null,
                    weekIndex: null
                });
            case RecurrencePatternType.FixedDayOfYear:
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.FixedDayOfYear,
                    interval,
                    month: resource.month,
                    dayOfMonth: resource.dayOfMonth,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    weekIndex: null
                });
            case RecurrencePatternType.FixedDayOfMonth:
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.FixedDayOfMonth,
                    interval,
                    dayOfMonth: resource.dayOfMonth,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    month: null,
                    weekIndex: null
                });
            case RecurrencePatternType.RelativeDayOfYear:
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.RelativeDayOfYear,
                    interval,
                    month: resource.month,
                    weekIndex: resource.index,
                    dayOfWeek: resource.daysOfWeek[0],
                    dayOfMonth: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null
                });
            case RecurrencePatternType.RelativeDayOfMonth:
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.RelativeDayOfMonth,
                    interval,
                    weekIndex: resource.index,
                    dayOfWeek: resource.daysOfWeek[0],
                    dayOfMonth: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    month: null
                });
            case RecurrencePatternType.Weekly:
                if (resource.daysOfWeek.length === 1) {
                    return new RecurrencePattern({
                        patternType: RecurrencePatternType.Weekly,
                        interval,
                        dayOfWeek: resource.daysOfWeek[0],
                        firstDayOfWeek: resource.firstDayOfWeek,
                        dayOfMonth: null,
                        daysOfWeek: null,
                        month: null,
                        weekIndex: null
                    });
                } else if (resource.interval === 1) {
                    return new RecurrencePattern({
                        patternType: RecurrencePatternType.Weekly,
                        interval: 1,
                        daysOfWeek: resource.daysOfWeek,
                        firstDayOfWeek: resource.firstDayOfWeek,
                        dayOfMonth: null,
                        dayOfWeek: null,
                        month: null,
                        weekIndex: null
                    });
                } else {
                    throw new Error(`Invalid RecurrencePattern configuration: For multiple days in a week the interval must be 1 when patternType is ${RecurrencePatternType.Weekly}`);
                }
            default:
                ErrorUtilities.unreachableCase(resource.type, ()=>{
                // Log function - no traceLogger available in this context
                });
                throw new Error(`Unsupported recurrence pattern type: ${resource.type}`);
        }
    }
    getClone() {
        // Since forSerialization creates a clone of reference types, we can leverage it here.
        return RecurrencePattern.fromResource(this.forSerialization());
    }
    /**
     * Get the serializable representation of the entity for general purposes. This must create a clone of reference types.
     */ forSerialization() {
        // Graph expects defaults for unused properties per type. We store nulls, thus we need to simply default when null.
        return {
            dayOfMonth: this.dayOfMonth ?? 0,
            daysOfWeek: Array.from(this.daysOfWeek ?? []),
            firstDayOfWeek: this.firstDayOfWeek ?? DayOfWeek.Sunday,
            interval: this.interval,
            month: this.month ?? 0,
            type: this.patternType,
            index: this.weekIndex ?? RelativeWeekIndex.First
        };
    }
    generateHash() {
        const result = [
            this.patternType,
            this.interval.toString()
        ];
        switch(this.patternType){
            case RecurrencePatternType.RelativeDayOfYear:
                result.push(this.month.toString());
                result.push(this.weekIndex.toString());
                result.push(this.daysOfWeek.toString());
                break;
            case RecurrencePatternType.RelativeDayOfMonth:
                result.push(this.weekIndex.toString());
                result.push(this.daysOfWeek.toString());
                break;
            case RecurrencePatternType.Weekly:
                result.push(this.daysOfWeek.toString());
                break;
            case RecurrencePatternType.FixedDayOfYear:
                result.push(this.month.toString());
                result.push(this.dayOfMonth.toString());
                break;
            case RecurrencePatternType.FixedDayOfMonth:
                result.push(this.dayOfMonth.toString());
                break;
            case RecurrencePatternType.Daily:
            default:
                break;
        }
        return result.join(",");
    }
    constructor(propertyBag){
        // Start with nulls for all optional properties
        this.dayOfMonth = null;
        this.daysOfWeek = null;
        this.firstDayOfWeek = null;
        this.month = null;
        this.weekIndex = null;
        // Set the required properties
        this.patternType = propertyBag.patternType;
        this.interval = propertyBag.interval;
        switch(propertyBag.patternType){
            case RecurrencePatternType.Daily:
                if (propertyBag.dayOfMonth !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfMonth must be null when patternType is ${RecurrencePatternType.Daily}`);
                }
                if (propertyBag.dayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfWeek must be null when patternType is ${RecurrencePatternType.Daily}`);
                }
                if (propertyBag.daysOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: daysOfWeek must be null when patternType is ${RecurrencePatternType.Daily}`);
                }
                if (propertyBag.firstDayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: firstDayOfWeek must be null when patternType is ${RecurrencePatternType.Daily}`);
                }
                if (propertyBag.month !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: month must be null when patternType is ${RecurrencePatternType.Daily}`);
                }
                if (propertyBag.weekIndex !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: weekIndex must be null when patternType is ${RecurrencePatternType.Daily}`);
                }
                break;
            case RecurrencePatternType.FixedDayOfYear:
                if (propertyBag.dayOfMonth === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfMonth is required when patternType is ${RecurrencePatternType.FixedDayOfYear}`);
                }
                if (propertyBag.dayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfWeek must be null when patternType is ${RecurrencePatternType.FixedDayOfYear}`);
                }
                if (propertyBag.daysOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: daysOfWeek must be null when patternType is ${RecurrencePatternType.FixedDayOfYear}`);
                }
                if (propertyBag.firstDayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: firstDayOfWeek must be null when patternType is ${RecurrencePatternType.FixedDayOfYear}`);
                }
                if (propertyBag.month === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: month is required when patternType is ${RecurrencePatternType.FixedDayOfYear}`);
                }
                if (propertyBag.weekIndex !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: weekIndex must be null when patternType is ${RecurrencePatternType.FixedDayOfYear}`);
                }
                this.month = propertyBag.month;
                this.dayOfMonth = propertyBag.dayOfMonth;
                break;
            case RecurrencePatternType.FixedDayOfMonth:
                if (propertyBag.dayOfMonth === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfMonth is required when patternType is ${RecurrencePatternType.FixedDayOfMonth}`);
                }
                if (propertyBag.dayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfWeek must be null when patternType is ${RecurrencePatternType.FixedDayOfMonth}`);
                }
                if (propertyBag.daysOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: daysOfWeek must be null when patternType is ${RecurrencePatternType.FixedDayOfMonth}`);
                }
                if (propertyBag.firstDayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: firstDayOfWeek must be null when patternType is ${RecurrencePatternType.FixedDayOfMonth}`);
                }
                if (propertyBag.month !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: month must be null when patternType is ${RecurrencePatternType.FixedDayOfMonth}`);
                }
                if (propertyBag.weekIndex !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: weekIndex must be null when patternType is ${RecurrencePatternType.FixedDayOfMonth}`);
                }
                this.dayOfMonth = propertyBag.dayOfMonth;
                break;
            case RecurrencePatternType.RelativeDayOfYear:
                if (propertyBag.dayOfMonth !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfMonth must be null when patternType is ${RecurrencePatternType.RelativeDayOfYear}`);
                }
                if (propertyBag.dayOfWeek === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfWeek is required when patternType is ${RecurrencePatternType.RelativeDayOfYear}`);
                }
                if (propertyBag.daysOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: daysOfWeek must be null when patternType is ${RecurrencePatternType.RelativeDayOfYear}`);
                }
                if (propertyBag.firstDayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: firstDayOfWeek must be null when patternType is ${RecurrencePatternType.RelativeDayOfYear}`);
                }
                if (propertyBag.month === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: month is required when patternType is ${RecurrencePatternType.RelativeDayOfYear}`);
                }
                if (propertyBag.weekIndex === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: weekIndex is required when patternType is ${RecurrencePatternType.RelativeDayOfYear}`);
                }
                this.month = propertyBag.month;
                this.weekIndex = propertyBag.weekIndex;
                this.daysOfWeek = [
                    propertyBag.dayOfWeek
                ];
                break;
            case RecurrencePatternType.RelativeDayOfMonth:
                if (propertyBag.dayOfMonth !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfMonth must be null when patternType is ${RecurrencePatternType.RelativeDayOfMonth}`);
                }
                if (propertyBag.dayOfWeek === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfWeek is required when patternType is ${RecurrencePatternType.RelativeDayOfMonth}`);
                }
                if (propertyBag.daysOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: daysOfWeek must be null when patternType is ${RecurrencePatternType.RelativeDayOfMonth}`);
                }
                if (propertyBag.firstDayOfWeek !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: firstDayOfWeek must be null when patternType is ${RecurrencePatternType.RelativeDayOfMonth}`);
                }
                if (propertyBag.month !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: month must be null when patternType is ${RecurrencePatternType.RelativeDayOfMonth}`);
                }
                if (propertyBag.weekIndex === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: weekIndex is required when patternType is ${RecurrencePatternType.RelativeDayOfMonth}`);
                }
                this.weekIndex = propertyBag.weekIndex;
                this.daysOfWeek = [
                    propertyBag.dayOfWeek
                ];
                break;
            case RecurrencePatternType.Weekly:
                if (propertyBag.dayOfMonth !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: dayOfMonth must be null when patternType is ${RecurrencePatternType.Weekly}`);
                }
                if (propertyBag.dayOfWeek !== null && propertyBag.daysOfWeek !== null || propertyBag.dayOfWeek === null && propertyBag.daysOfWeek === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: One of dayOfWeek or daysOfWeek must be set when patternType is ${RecurrencePatternType.Weekly}`);
                }
                if (propertyBag.firstDayOfWeek === null) {
                    throw new Error(`Invalid RecurrencePattern configuration: firstDayOfWeek is required when patternType is ${RecurrencePatternType.Weekly}`);
                }
                if (propertyBag.month !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: month must be null when patternType is ${RecurrencePatternType.Weekly}`);
                }
                if (propertyBag.weekIndex !== null) {
                    throw new Error(`Invalid RecurrencePattern configuration: weekIndex must be null when patternType is ${RecurrencePatternType.Weekly}`);
                }
                this.daysOfWeek = propertyBag.daysOfWeek ?? [
                    propertyBag.dayOfWeek
                ];
                if (isEmpty(this.daysOfWeek)) {
                    throw new Error(`Invalid RecurrencePattern configuration: daysOfWeek must be a non-empty array when patternType is ${RecurrencePatternType.Weekly}`);
                }
                this.firstDayOfWeek = propertyBag.firstDayOfWeek;
                break;
            default:
                throw new Error(`Unsupported recurrence pattern type: ${propertyBag["patternType"]}`);
        }
    }
}
